import { AlertsService } from '../../../../services/alerts.service';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {CopilotWizardTileComponent} from "../../components/copilot-wizard-tile/copilot-wizard-tile.component";
import {CopilotMoreWizardOptionsTileComponent} from "../../components/copilot-more-wizard-options-tile/copilot-more-wizard-options-tile.component";
import {CopilotPromptBarComponent} from "../../components/copilot-prompt-bar/copilot-prompt-bar.component";
import {FormControl} from '@angular/forms';
import {TwoStageBrandPickerComponent} from "../../components/two-stage-brand-picker/two-stage-brand-picker.component";
import { Wizard } from '../../types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, NgForOf } from '@angular/common';
import { StateService } from '../../../../services/state.service';
import { WizardComponent } from '../../components/wizard/wizard.component';
import { WizardSubmitEvent } from '../../copilot.model';
import { Router } from '@angular/router';
import { PolarisRoutePaths, RoutePaths } from '../../../../shared/types/common';
import { MatTooltip } from '@angular/material/tooltip';
import { tooltips } from '../../../../utils/tooltips';
import { PosthogService } from '../../../../services/posthog.service';
import { PosthogEventName, PosthogEventCode } from '../../../../services/types';
import { AuthorizationService } from '../../../../services/authorization.service';
import { DashboardService } from '../../../../services/dashboard.service';
import {PolarisRuntimeFacadeService} from '../../../../services/polaris/polaris-runtime-facade.service';
import {PolarisModelVersion, PolarisStartChatOptionsVm} from '../../../../services/polaris/model';
import {Permissions} from '../../../../shared/permissions';

const PolarisToolNameMap: Record<string, string> = {
  'alert_center': 'Alert Center',
  'creative_brief': 'Creative Brief',
  'campaign_review': 'Campaign Review',
  'audience_research': 'Audience Research',
  'data_snapshot': 'Data Snapshot',
};

const PolarisToolKeyMap: Record<string, string> = Object.fromEntries(
  Object.entries(PolarisToolNameMap).map(([key, value]) => [value, key])
);

const NUMBER_OF_WIZARD_TILES = 4;

@Component({
  selector: 'app-copilot-home',
  imports: [
    CopilotWizardTileComponent,
    CopilotMoreWizardOptionsTileComponent,
    CopilotPromptBarComponent,
    TwoStageBrandPickerComponent,
    NgForOf,
    WizardComponent,
    MatTooltip,
    CommonModule
  ],
  templateUrl: './copilot-home.component.html',
  styleUrl: './copilot-home.component.scss'
})

export class CopilotHomeComponent implements OnInit {
  public promptControl = new FormControl('');
  private authorizationService = inject(AuthorizationService);
  private alertsService = inject(AlertsService);
  private dashboardService = inject(DashboardService);
  private destroyRef = inject(DestroyRef);
  private stateService = inject(StateService);
  private router = inject(Router);
  private posthogService = inject(PosthogService);
  private polarisRuntimeFacadeService = inject(PolarisRuntimeFacadeService);
  public isWizardOpen: boolean = false;
  public readonly userFirstName = this.polarisRuntimeFacadeService.state.customerName;
  private readonly recentChatId = this.polarisRuntimeFacadeService.state.recentChatId;
  public selectedModelVersion = signal<PolarisModelVersion>('v1');
  public canSelectV2 = this.authorizationService.hasPermissions([Permissions.SEND_POLARIS_V2_MESSAGE]);
  public wizards: Wizard[] = [];
  public numberOfWizardTiles: number = NUMBER_OF_WIZARD_TILES;
  public selectedWizardIndex: number = 0;
  protected readonly tooltips = tooltips;
  protected hasAlertCenterPermissions: boolean = false;

  // Observable for watching brandsOptions (initialized in injection context)
  private brandsOptions$ = toObservable(this.dashboardService.brandsOptions);


  ngOnInit(): void {
    const recentChatId = this.recentChatId();
    if (recentChatId) {
      this.navigateToChat(recentChatId);
      return;
    }

    this.stateService.loading = true;

    if (this.authorizationService.isTrialUser) {
      this.brandsOptions$.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(brandsOptions => {
        const myBrands = brandsOptions.myBrands;
        if (myBrands && myBrands.length > 0 && !this.polarisRuntimeFacadeService.state.myBrand()) {
          this.presetTrialUserBrands();
        }
      });
    } else {
      this.presetEmptyBrands();
    }

    this.polarisRuntimeFacadeService.getWizardsResponse().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(wizards => {
      this.wizards = wizards.map(wizard => ({
        ...wizard,
        tileSvg: wizard.tileSvg ?? '',
        wizardSvg: wizard.wizardSvg ?? ''
      }));
      this.stateService.loading = false;
    });
    this.hasAlertCenterPermissions = this.alertsService.hasPermissions();
  }

  openAlertCenter(): void {
    if (this.hasAlertCenterPermissions) {
      this.posthogService.capture(PosthogEventName.POLARIS_TOOL_SELECTED, {
        [PosthogEventCode.TOOL_NAME]: 'alert_center'
      });
      this.router.navigate([RoutePaths.ALERT_CENTER]);
    }
  }

  openWizard(id: number): void {
    if (this.isExpiredUser) return;
    const wizardTitle = this.wizards[id]?.title ?? '';
    const toolKey = PolarisToolKeyMap[wizardTitle] ?? wizardTitle;
    this.posthogService.capture(PosthogEventName.POLARIS_TOOL_SELECTED, {
      [PosthogEventCode.TOOL_NAME]: toolKey
    });
    this.isWizardOpen = true;
    this.selectedWizardIndex = id;
  }

  closeWizard(): void {
    if (this.isExpiredUser) return;
    this.isWizardOpen = false;
    this.selectedWizardIndex = 0;
  }

  onSendMessage(message: string, options?: PolarisStartChatOptionsVm): void {
    if (this.isExpiredUser) return;
    const myBrand = this.polarisRuntimeFacadeService.state.myBrand();
    const myCompetitors = this.polarisRuntimeFacadeService.state.myCompetitors();
    const startChatOptions: PolarisStartChatOptionsVm = {
      ...options,
      modelVersion: options?.modelVersion ?? this.selectedModelVersion(),
      origin: options?.origin ?? 'polaris_homepage'
    };

    this.polarisRuntimeFacadeService.setUserBrandContext(myBrand, myCompetitors);
    this.posthogService.capture(PosthogEventName.POLARIS_PROMPT_SENT, {
      [PosthogEventCode.MY_BRAND]: myBrand ?? '',
      [PosthogEventCode.MY_COMPETITORS]: myCompetitors.join(';'),
      [PosthogEventCode.ORIGIN]: startChatOptions.origin,
      [PosthogEventCode.PROMPT_CONTENT]: message
    });
    void this.startChatAndNavigate(message, startChatOptions);
  }

  onWizardSubmit(event: WizardSubmitEvent): void {
    if (this.isExpiredUser) return;
    this.polarisRuntimeFacadeService.setUserBrandContext(event.brand, event.competitors);
    this.onSendMessage(event.message, {
      ...event.options,
      origin: 'polaris_wizard'
    });
  }

  onModelVersionChange(version: PolarisModelVersion): void {
    this.selectedModelVersion.set(version);
  }

  get isExpiredUser(): boolean {
    return this.authorizationService.isExpiredUser;
  }

  private presetEmptyBrands(): void {
    this.polarisRuntimeFacadeService.setUserBrandContext(null, []);
  }

  private presetTrialUserBrands(): void {
    const brandsOptions = this.dashboardService.brandsOptions();
    const myBrands = brandsOptions.myBrands!;
    const brandKey = myBrands[0].key;
    const brandCompetitors = brandsOptions.brandCompetitorsResponse?.[brandKey];
    const competitorKeys = brandCompetitors?.competitors?.map(competitor => competitor.key) || [];
    this.polarisRuntimeFacadeService.setUserBrandContext(brandKey, competitorKeys);
  }

  private navigateToChat(chatId: string): void {
    this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.CHAT}/${chatId}`]);
  }

  private async startChatAndNavigate(message: string, options: PolarisStartChatOptionsVm): Promise<void> {
    try {
      const chatId = await this.polarisRuntimeFacadeService.startChat(message, options);
      this.navigateToChat(chatId);
    } catch {
      // Keep the user on home when chat creation fails.
    }
  }

}
